import { useMemo, useState } from 'react';
import Select, { MultiValue, MultiValueGenericProps, components } from 'react-select';
import {
  ADD_NEW_VALUE,
  addNewOption,
  convertFromValueLabelListIds,
  convertToValueLabelListIds,
  NumberOption,
  removeNewOption,
} from '../utils/compare-functions';
import CreateNewList from '../components/CreateNewList';
import { IUserListRef } from '../types/company';

const CustomOption = (props: any) => {
  const commonClasses = 'font-bold text-sm cursor-pointer';
  const addNewClasses = `${commonClasses} text-ow-secondary cursor-pointer block border-b pb-3`;

  return (
    <div>
      <components.Option {...props}>
        {props.value !== ADD_NEW_VALUE && (
          <input type="checkbox" checked={props.isSelected} onChange={() => null} className="mr-2 cursor-pointer" />
        )}
        <label className={props.value === ADD_NEW_VALUE ? addNewClasses : commonClasses}>{props.label}</label>
      </components.Option>
    </div>
  );
};

const CustomMultiValueContainer = (props: MultiValueGenericProps<NumberOption>) => {
  const values = props.selectProps.value as NumberOption[];

  if (values.length > 5) return <div className="fss-multiple-list-item">{`In ${values.length} lists...`}</div>;

  if (values) {
    return values[values.length - 1].label === props.data.label ? props.data.label : props.data.label + ', ';
  } else return '';
};

const CustomDropdownIndicator = (props: any) => {
  return (
    <div {...props.innerProps}>
      <img src="/icons/filter-sort.svg" alt="Filter / sort icon" className="ml-1" />
    </div>
  );
};

export default function MultiSelectAddNewItemDropdown({
  initialUserList,
  userLists,
  placeHolder,
  width = '300px',
  customIndicator = true,
  onSelectionChange,
  onCreateNewListName,
}: Readonly<{
  initialUserList: number[]; // selected list IDs
  userLists: IUserListRef[];
  placeHolder: string;
  width?: string;
  customIndicator?: boolean;
  onSelectionChange: (selectedListIds: number[]) => void;
  onCreateNewListName?: (newListName: string) => void;
}>) {
  const selectedOptions = useMemo<MultiValue<NumberOption>>(
    () => convertToValueLabelListIds(initialUserList, userLists),
    [initialUserList, userLists]
  );

  const options = useMemo<number[]>(() => userLists.map((list) => list.id), [userLists]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const onModalClose = (): void => {
    setIsModalOpen(false);
  };

  const commitSelected = (nextSelected: MultiValue<NumberOption>) => {
    const cleaned = removeNewOption(nextSelected);
    onSelectionChange(convertFromValueLabelListIds(cleaned));
  };

  const onModalSubmit = (newLabel: string): void => {
    setIsModalOpen(false);

    //remove csv challenging values
    newLabel = newLabel.trim().replace(/\s+/g, ' ').replace(/"/g, '').replace(/,/g, '');
    if (newLabel.length > 0) {
      onCreateNewListName?.(newLabel);
    }
  };

  const handleChange = (selectedList: MultiValue<NumberOption>) => {
    const index = selectedList.findIndex((item) => item.value === ADD_NEW_VALUE);
    if (index !== -1) {
      setIsModalOpen(true);
      return;
    }

    commitSelected(selectedList);
  };

  const styles = {
    option: (provided: any) => ({
      ...provided,
      background: 'none',
      color: '#343D40', // ow-darker-grey
      paddingBottom: 0,
      paddingTop: 0,
      marginTop: 10,
      marginBottom: 10,
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: 'none',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: '#2C6EF2', // ow-secondary,
    }),
    control: (provided: any) => ({
      ...provided,
      border: 'none',
      cursor: 'pointer',
      boxShadow: 'none',
      minHeight: '32px',
      paddingLeft: '0.5rem',
      ...(customIndicator && { paddingRight: '0.5rem' }),
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      display: 'none',
    }),
    multiValue: (provided: any) => ({
      ...provided,
      background: 'none',
      fontSize: '0.875rem',
      padding: 0,
      border: 0,
      margin: 0,
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      fontSize: '100%',
      padding: 0,
      paddingLeft: 0,
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: 0,
      whiteSpace: 'nowrap',
      overflowX: 'hidden',
      textOverflow: 'ellipsis',
      display: 'block',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      position: 'absolute',
      ...(customIndicator && { fontStyle: 'italic' }),
    }),
  };

  return (
    <div style={{ width: width }}>
      <Select
        isMulti
        styles={styles}
        value={selectedOptions}
        onChange={handleChange}
        options={addNewOption(convertToValueLabelListIds(options, userLists))}
        placeholder={placeHolder}
        hideSelectedOptions={false}
        isClearable={false}
        isSearchable={false}
        components={{
          Option: CustomOption,
          MultiValueContainer: CustomMultiValueContainer,
          ...(customIndicator && { DropdownIndicator: CustomDropdownIndicator }),
        }}
        className={'add-new-item-dropdown'}
        classNamePrefix={'add-new-item-dropdown'}
      />
      <CreateNewList isOpen={isModalOpen} onClose={onModalClose} onSubmit={onModalSubmit} />
    </div>
  );
}
